import { toast } from "@arcadeai/design-system/components/ui/molecules/toast";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import { inboxBadgeLabel } from "../lib/inbox-badge";
import { PROJECT_SUGGESTIONS_DEFAULT_PAGE_SIZE } from "../search-params";
import type {
  ProjectSuggestionListParams,
  ResolveGatewaySuggestionRequest,
} from "../types";
import { projectSuggestionsQueryOptions } from "./get-project-suggestions";
import { projectSuggestionsKeys } from "./query-keys";
import { resolveGatewaySuggestion } from "./resolve-gateway-suggestion";

export const useProjectSuggestions = (params: ProjectSuggestionListParams) => {
  const scope = useResourceScope();

  return useQuery({
    ...projectSuggestionsQueryOptions({ scope, params }),
    placeholderData: keepPreviousData,
  });
};

/** Whether the project has any suggestions at all, open or responded. */
export const useAnyProjectSuggestions = (enabled: boolean) => {
  const scope = useResourceScope();
  const { data, isLoading } = useQuery({
    ...projectSuggestionsQueryOptions({
      scope,
      params: { show_resolved: true, limit: 1, offset: 0 },
    }),
    enabled,
  });

  return {
    anySuggestions: (data?.totalCount ?? 0) > 0,
    isLoading: enabled && isLoading,
  };
};

/** Open suggestions across the project, capped at one inbox page for display. */
export const useOpenSuggestionsBadge = () => {
  const scope = useResourceScope();
  const { data, isLoading } = useQuery({
    ...projectSuggestionsQueryOptions({
      scope,
      params: { show_resolved: false, limit: 1, offset: 0 },
    }),
    retry: false,
  });
  const count = data?.totalCount ?? 0;

  return {
    count,
    isLoading,
    label: inboxBadgeLabel({
      count,
      cap: PROJECT_SUGGESTIONS_DEFAULT_PAGE_SIZE,
    }),
  };
};

export const useResolveGatewaySuggestion = () => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, "project");

  return useMutation({
    mutationFn: ({
      gatewayId,
      suggestionId,
      payload = {},
    }: {
      gatewayId: string;
      suggestionId: string;
      payload?: ResolveGatewaySuggestionRequest;
    }) =>
      resolveGatewaySuggestion({
        client,
        gatewayId,
        suggestionId,
        payload,
      }),
    meta: {
      awaits: [projectSuggestionsKeys.all(scope)],
      errorMessage: "Unable to respond to suggestion",
    },
    onSuccess: () => {
      toast.success("Response sent");
    },
  });
};
